// Preview and download step - shows the selected output and allows download.
//

import { ArrowLeft, Download, Heart, Plus, RotateCcw, X } from "lucide-react";
import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/ui/button";

// Hook to get donation link from public env (set via STRIPE_DONATION_LINK).
// Returns empty string during SSR and initial render to avoid hydration mismatch.
//
function useDonationLink(): string {
  const [link, setLink] = useState("");

  useEffect(() => {
    const publicEnv = (
      window as unknown as { publicEnv?: { stripeDonationLink?: string } }
    ).publicEnv;
    setLink(publicEnv?.stripeDonationLink ?? "");
  }, []);

  return link;
}

import {
  createPreviewDataUrl,
  downloadBlob,
  tileForPrint,
} from "../utils/canvas";
import {
  calculatePrintLayout,
  type OutputSize,
  PHOTO_SIZES,
  type PhotoSize,
  PRINT_SIZES,
} from "../utils/dimensions";

interface PreviewDownloadStepProps {
  croppedImages: Blob[];
  photoSize: PhotoSize;
  outputSize: OutputSize;
  photoGap: number;
  onTiledImageReady: (tiledImage: Blob) => void;
  onAddMorePhotos: (files: File[]) => void;
  onDownloadComplete: () => void;
  onStartOver: () => void;
  onBack: () => void;
}

// Preview and download step component.
//
export function PreviewDownloadStep({
  croppedImages,
  photoSize,
  outputSize,
  photoGap,
  onTiledImageReady,
  onAddMorePhotos,
  onDownloadComplete,
  onStartOver,
  onBack,
}: PreviewDownloadStepProps) {
  const [tiledBlob, setTiledBlob] = useState<Blob | null>(null);
  const [printPreviewUrl, setPrintPreviewUrl] = useState<string | null>(null);
  const [digitalPreviewUrls, setDigitalPreviewUrls] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(outputSize !== "digital");
  const [error, setError] = useState<string | null>(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [showDonationPrompt, setShowDonationPrompt] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const donationLink = useDonationLink();
  const photoSizeConfig = PHOTO_SIZES[photoSize];
  const printOutput =
    outputSize === "digital"
      ? null
      : {
          size: outputSize,
          config: PRINT_SIZES[outputSize],
          layout: calculatePrintLayout(outputSize, photoSize, photoGap),
        };
  const maxPhotos = printOutput?.layout.photoCount ?? croppedImages.length;
  const hasEmptySlots =
    printOutput !== null && croppedImages.length < maxPhotos;
  const digitalPhotos = croppedImages.map((blob, index) => {
    const suffix = croppedImages.length > 1 ? `-${index + 1}` : "";

    return {
      blob,
      filename: `passport-photo-${photoSize}${suffix}.png`,
      label:
        croppedImages.length > 1
          ? `Download Photo ${index + 1}`
          : "Download Digital Photo",
    };
  });

  // Create local preview URLs for individual digital photos.
  //
  useEffect(() => {
    if (outputSize !== "digital") {
      setDigitalPreviewUrls([]);
      return;
    }

    const urls = croppedImages.map((image) => URL.createObjectURL(image));
    setDigitalPreviewUrls(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [croppedImages, outputSize]);

  // Generate a tiled image only when a paper size is selected.
  //
  useEffect(() => {
    let cancelled = false;

    if (outputSize === "digital") {
      setTiledBlob(null);
      setPrintPreviewUrl(null);
      setIsProcessing(false);
      setError(null);
      return;
    }

    const selectedPrintSize = outputSize;

    async function generateTiledImage() {
      if (croppedImages.length === 0) {
        setIsProcessing(false);
        return;
      }

      setIsProcessing(true);
      setPrintPreviewUrl(null);
      setError(null);

      try {
        const tiled = await tileForPrint(
          croppedImages,
          selectedPrintSize,
          photoSize,
          photoGap,
        );

        if (cancelled) return;

        setTiledBlob(tiled);
        onTiledImageReady(tiled);

        const url = await createPreviewDataUrl(tiled);

        if (cancelled) return;

        setPrintPreviewUrl(url);
      } catch (err) {
        console.error("Failed to generate tiled image:", err);
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to generate preview",
          );
        }
      } finally {
        if (!cancelled) {
          setIsProcessing(false);
        }
      }
    }

    void generateTiledImage();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onTiledImageReady is stable
  }, [croppedImages, outputSize, photoSize, photoGap]);

  // Download the selected print layout.
  //
  const handlePrintDownload = () => {
    if (tiledBlob && printOutput) {
      const filename = `passport-photos-${photoSize}-${printOutput.size}.png`;
      downloadBlob(tiledBlob, filename);
      setHasDownloaded(true);
      onDownloadComplete();
    }
  };

  // Download one cropped photo at the selected photo dimensions.
  //
  const handleDigitalDownload = (blob: Blob, filename: string) => {
    downloadBlob(blob, filename);
    setHasDownloaded(true);
    onDownloadComplete();
  };

  // Handle adding more photos.
  //
  const handleAddMore = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onAddMorePhotos(files);
    }
    e.target.value = "";
  };

  // Show message if no cropped images.
  //
  if (croppedImages.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-lg">No photos have been cropped yet.</p>
          <p className="text-muted-foreground mt-2 text-sm">
            Go back and click &quot;Save Crop&quot; on each photo.
          </p>
          <Button className="mt-4" variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Crop
          </Button>
        </div>
      </div>
    );
  }

  // Show print-generation errors.
  //
  if (printOutput && error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-lg text-red-600 dark:text-red-400">
            Error generating preview
          </p>
          <p className="text-muted-foreground mt-2 text-sm">{error}</p>
          <Button className="mt-4" variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (printOutput && (isProcessing || !printPreviewUrl)) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-muted-foreground mt-4">
            Preparing your print layout...
          </p>
          <p className="text-muted-foreground mt-2 text-xs">
            {croppedImages.length} photo(s) to process
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {printOutput && (
        <input
          ref={fileInputRef}
          multiple
          accept="image/*"
          className="hidden"
          type="file"
          onChange={handleFileInputChange}
        />
      )}

      <div className="text-center">
        <h3 className="text-xl font-semibold">
          {printOutput
            ? "Your Print Is Ready!"
            : croppedImages.length === 1
              ? "Your Digital Photo Is Ready!"
              : "Your Digital Photos Are Ready!"}
        </h3>
        {printOutput ? (
          <>
            <p className="text-muted-foreground mt-1">
              Download and print on {printOutput.config.label} paper.
            </p>
            <p className="text-muted-foreground mt-1 text-sm">
              {croppedImages.length} of {maxPhotos} slots filled
            </p>
          </>
        ) : (
          <p className="text-muted-foreground mt-1">
            Each PNG is {photoSizeConfig.pixels.width} ×{" "}
            {photoSizeConfig.pixels.height} pixels.
          </p>
        )}
      </div>

      {printOutput ? (
        <>
          <div className="flex justify-center">
            <div className="overflow-hidden border bg-white shadow-lg">
              <img
                alt="Print preview"
                className="h-auto max-h-96 w-auto max-w-full"
                src={printPreviewUrl ?? undefined}
              />
            </div>
          </div>

          {hasEmptySlots && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleAddMore}>
                <Plus className="mr-2 h-4 w-4" />
                Add More Photos ({maxPhotos - croppedImages.length} slots
                available)
              </Button>
            </div>
          )}

          <div className="bg-accent rounded-xl border p-4">
            <div className="flex items-start gap-3">
              <div className="bg-primary text-primary-foreground flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
                <Download className="h-3 w-3" />
              </div>
              <div>
                <h4 className="font-medium">Print at 100% scale</h4>
                <p className="text-muted-foreground mt-1 text-sm">
                  For accurate sizing, set your printer to 100% (no scaling).
                  The image is {printOutput.config.pixels.width} ×{" "}
                  {printOutput.config.pixels.height} pixels at 300 DPI.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <Button size="lg" onClick={handlePrintDownload}>
              <Download className="mr-2 h-5 w-5" />
              Download {printOutput.config.label} Print
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {digitalPhotos.map((photo, index) => (
              <div
                key={photo.filename}
                className="overflow-hidden rounded-xl border"
              >
                <div className="bg-accent flex min-h-52 items-center justify-center p-4">
                  {digitalPreviewUrls[index] ? (
                    <img
                      alt={`Digital passport photo ${index + 1}`}
                      className="max-h-72 max-w-full border bg-white object-contain shadow-sm"
                      src={digitalPreviewUrls[index]}
                    />
                  ) : (
                    <div className="border-primary h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
                  )}
                </div>
                <div className="p-4 text-center">
                  <Button
                    size="lg"
                    onClick={() =>
                      handleDigitalDownload(photo.blob, photo.filename)
                    }
                  >
                    <Download className="mr-2 h-5 w-5" />
                    {photo.label}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-accent rounded-xl border p-4">
            <div className="flex items-start gap-3">
              <div className="bg-primary text-primary-foreground flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
                <Download className="h-3 w-3" />
              </div>
              <div>
                <h4 className="font-medium">Digital photo</h4>
                <p className="text-muted-foreground mt-1 text-sm">
                  Each download contains one {photoSizeConfig.label} photo.
                  Check the issuing authority&apos;s required file format and
                  file size before submitting it.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {hasDownloaded && showDonationPrompt && donationLink && (
        <div className="bg-accent relative rounded-xl border p-4">
          <button
            aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground absolute top-2 right-2"
            type="button"
            onClick={() => setShowDonationPrompt(false)}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="bg-primary text-primary-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
              <Heart className="h-4 w-4" />
            </div>
            <div className="pr-6">
              <h4 className="font-medium">Glad this helped!</h4>
              <p className="text-muted-foreground mt-1 text-sm">
                This tool is free and processes everything privately on your
                device. If it saved you a trip to the store, consider supporting
                its development.
              </p>
              <a
                className="text-primary hover:text-primary/80 mt-2 inline-flex items-center gap-1 text-sm font-medium underline"
                href={donationLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Heart className="h-3 w-3" />
                Support this project
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between border-t pt-6">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button variant="outline" onClick={onStartOver}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Start Over
        </Button>
      </div>
    </div>
  );
}
